import { isPaymentMode, type PaymentMode } from '../types'
import type { WidgetBootstrapResponse } from '../types/PublicBookingChannelTypes'
import { formatPounds, normalizePrice } from './format'

export interface PaymentPlan {
  mode: PaymentMode
  /** Amount due now, in pounds. Null when nothing is taken online. */
  amountDue: number | null
  /** Full basket value, for display alongside a deposit. */
  total: number
  /** Copy for the primary button. */
  actionLabel: string
}

/**
 * Resolves what the customer pays at the point of booking.
 *
 * The previous implementation ignored the channel's payment configuration
 * entirely and redirected every booking to Stripe Checkout, so a garage that
 * had switched payments off still forced its customers to pay — and a garage
 * with no Stripe account produced a booking followed by an error. Payment
 * mode is now read from the channel, with the legacy `paymentsEnabled` flag
 * honoured for backends that predate it.
 */
export function resolvePaymentMode(channel: WidgetBootstrapResponse.Channel | null): PaymentMode {
  if (!channel) return 'none'

  if (isPaymentMode(channel.paymentMode)) {
    return channel.paymentMode
  }

  // Older backend: a boolean is all we have.
  return channel.paymentsEnabled ? 'full' : 'none'
}

function resolveDepositAmount(
  channel: WidgetBootstrapResponse.Channel,
  total: number
): number | null {
  const fixed = normalizePrice(channel.depositAmount)
  if (fixed != null && fixed > 0) {
    // Never ask for more than the job is worth.
    return Math.min(fixed, total)
  }

  const percent = normalizePrice(channel.depositPercent)
  if (percent != null && percent > 0) {
    const bounded = Math.min(percent, 100)
    return Math.round(total * bounded) / 100
  }

  return null
}

/**
 * Works out the payment plan for a basket.
 *
 * A deposit channel with no deposit configured, or a basket with no priced
 * items, falls back to taking nothing rather than sending the customer to a
 * checkout for £0.
 */
export function buildPaymentPlan(
  channel: WidgetBootstrapResponse.Channel | null,
  total: number
): PaymentPlan {
  const mode = resolvePaymentMode(channel)
  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0

  if (mode === 'none' || safeTotal <= 0) {
    return { mode: 'none', amountDue: null, total: safeTotal, actionLabel: 'Confirm booking' }
  }

  if (mode === 'full') {
    return { mode: 'full', amountDue: safeTotal, total: safeTotal, actionLabel: 'Pay & book' }
  }

  const deposit = channel ? resolveDepositAmount(channel, safeTotal) : null
  if (deposit == null || deposit <= 0) {
    return { mode: 'none', amountDue: null, total: safeTotal, actionLabel: 'Confirm booking' }
  }

  return { mode: 'deposit', amountDue: deposit, total: safeTotal, actionLabel: 'Pay deposit & book' }
}

/** True when the booking flow must hand off to Stripe Checkout. */
export function requiresCheckout(plan: PaymentPlan): boolean {
  return plan.mode !== 'none' && (plan.amountDue ?? 0) > 0
}

/**
 * The refund rules in a sentence, shown before Checkout (ACA-44). Null on a
 * channel that sends none, which is one that takes no payment.
 */
export function cancellationPolicyText(
  policy: WidgetBootstrapResponse.Channel['cancellationPolicy']
): string | null {
  if (!policy) return null
  const pounds = normalizePrice(policy.keptUnderCutoff)
  const percent = normalizePrice(policy.keptUnderCutoffPercent)
  // A percentage deposit is never a promise of a full refund.
  const kept = pounds ? formatPounds(pounds) : percent ? `${percent}%` : null
  return kept
    ? `Free cancellation up to ${policy.fullRefundHoursBefore ?? 24} hours before your booking. After that the ${kept} deposit is kept. If the garage cancels, you get a full refund.`
    : 'If you or the garage cancel, you get a full refund.'
}
